import React, { useMemo, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  CellLeftV8,
  order2Sorting,
  toastSuccess,
  toastFailure,
  promiseModalDialog,
  TableDataState,
  EditCellV8,
  TableCellV8,
  TablePaginatedV8,
} from 'motion-components';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  isActiveColumn,
  lastDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
} from 'shared-pages/common/grid-helpfunc';
import { useMutation } from '@apollo/client';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import * as Lo from 'lodash';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    dataCount,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateProfession] = useMutation(gqlUpdate);
  const [addNewProfession] = useMutation(gqlAdd);
  const [deleteProfession] = useMutation(gqlDelete);

  const columns = useMemo(
    () => [
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellLeftV8,
        size: 5,
      },
      {
        id: 'name',
        header: t('Επάγγελμα'),
        columnName: t('Επάγγελμα'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        meta: { type: 'text' },
        size: 50,
      },

      firstDateUserColumn(t),
      lastDateUserColumn(t),
      isActiveColumn(t, i18n),
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        size: 120,
        cell: ({ row, table }) => {
          return true ? EditCellV8({ row, table }) : <>-</>;
        },
      },
    ],
    [t],
  );

  const tableRef = useRef(null);

  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort, columnDef, orderBy) => {
            onOrderChange(orderBy);
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Professions - OnSaveRow row', row, changes);
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              const res = await addNewProfession({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addProfession.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateProfession({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε το Επάγγελμα <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) return;
            const res = await deleteProfession({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) return false; // some error returned
            refetch();
            toastSuccess('Διαγράφηκε το Επάγγελμα');
            return true;
          }}
          onNewRow={() => {
            return {
              id: -1,
              isActive: true,
            };
          }}
          topBorderRadius={false}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
